import React, { useState, useEffect } from "react";
import Editor from "@monaco-editor/react";
import * as monaco from "monaco-editor";
import myGif from '../assets/loading.gif';
import "./common.css";

// Manually import themes
import githubTheme from "monaco-themes/themes/GitHub.json";
import draculaTheme from "monaco-themes/themes/Dracula.json";
import monokaiTheme from "monaco-themes/themes/Monokai.json";
import solarizedDarkTheme from "monaco-themes/themes/Solarized-dark.json";
import solarizedLightTheme from "monaco-themes/themes/Solarized-light.json";

const CUSTOM_THEMES = {
  "github": githubTheme,
  "dracula": draculaTheme,
  "monokai": monokaiTheme,
  "solarized-dark": solarizedDarkTheme,
  "solarized-light": solarizedLightTheme,
};

const EditorLoadingSpinner = () => (
  <div className="absolute h-full inset-0 bg-white bg-opacity-90 flex items-center justify-center z-50">
    <div className="flex flex-col items-center gap-3">
      <img src={myGif} alt="Loading animation" className="w-[180px] h-[80px] object-contain" />
      <span className="text-sm text-gray-600">Loading...</span>
    </div>
  </div>
);

export default function CodeEditor({ language, code, theme, onChange, onEditorMount }) {
  const [isLoading, setIsLoading] = useState(true);
  const [currentTheme, setCurrentTheme] = useState(theme);

  // Define custom themes before editor mounts
  const handleBeforeMount = (monaco) => {
    // Define all custom themes
    Object.entries(CUSTOM_THEMES).forEach(([name, themeData]) => {
      try {
        monaco.editor.defineTheme(name, themeData);
      } catch (error) {
        console.warn(`Failed to define theme "${name}":`, error);
      }
    });
  };

  const handleMount = (editor) => {
    if (!editor) return;

    // Apply the current theme
    try {
      monaco.editor.setTheme(currentTheme);
    } catch (error) {
      console.warn(`Failed to set theme "${currentTheme}":`, error);
      // Fallback to default theme
      monaco.editor.setTheme('vs');
    }

    if (onEditorMount) onEditorMount(editor);

    editor.updateOptions({
      wordWrap: "on",
      automaticLayout: true,
      fontSize: 13,
      minimap: { enabled: false },
    });

    setIsLoading(false);
  };

  // Handle theme changes
  useEffect(() => {
    setCurrentTheme(theme);
    
    // If editor is already mounted, change theme immediately
    try {
      if (monaco?.editor) {
        monaco.editor.setTheme(theme);
      }
    } catch (error) {
      console.warn(`Failed to change theme to "${theme}":`, error);
    }
  }, [theme]);

  return (
    <div className="h-full relative">
      {isLoading && <EditorLoadingSpinner />}

      <Editor
        height="100%"
        language={language === "javascript" ? "javascript" : language}
        value={code}
        theme={currentTheme} // Let Monaco handle the theme
        loading={null} // disable default loading
        beforeMount={handleBeforeMount}
        onMount={handleMount}
        onChange={(value) => onChange(value ?? "")}
        options={{
          minimap: { enabled: false },
          fontSize: 13,
          automaticLayout: true,
          scrollBeyondLastLine: false,
          lineNumbers: "on",
        }}
      />
    </div>
  );
}